import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import type { AppComparisonPeriods } from "@/features/app-studio/lib/comparisonDisplay";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppKpiFormat = "number" | "percent" | "currency" | "text";

/**
 * Which widget table a KPI's drill-down target lives in. Required alongside the id because
 * charts and tables have independent id sequences, so chart 12 and table 12 both exist.
 */
export type AppKpiDrilldownItemType = "chart" | "table";
/**
 * Same target kinds whichever widget opens the drill-down, so table-column drill-downs reuse
 * this rather than declaring a parallel union.
 */
export type AppDrilldownItemType = AppKpiDrilldownItemType;

/**
 * How a drill-down puts its target on screen.
 *
 * `dialog` is the full-screen modal every drill-down opened before this existed, and is what an
 * absent value still means. `drawer` is a panel down the right-hand edge, which leaves the widget
 * that was clicked visible beside it — the reason to pick it. The width is a layout constant the
 * client owns, deliberately not stored per drill-down.
 */
export type AppDrilldownPresentation = "dialog" | "drawer";

/** Font-size presets for a KPI card's value and name. `md` is the card's standard size. */
export type AppKpiFontSize = "sm" | "md" | "lg" | "xl";

/** `between` is inclusive at both ends: `value <= metric <= value_to`. */
export type AppKpiRuleOperator = "gt" | "gte" | "lt" | "lte" | "eq" | "between";

/** Recolours the card when its numeric value matches. Needs `card_color`, `text_color` or both. */
export type AppKpiColorRule = {
  op: AppKpiRuleOperator;
  value: number;
  /** Upper bound, `between` only. */
  value_to?: number | null;
  card_color?: string | null;
  text_color?: string | null;
};

/**
 * What a KPI's change column holds: `percent` is already a % change, `amount` the plain
 * difference (shown in the card's format), `previous` last period's value (the chip shows the %
 * change from it).
 */
export type AppKpiChangeKind = "percent" | "amount" | "previous";

/**
 * The up/down chip under a KPI card's name. Its number is another column of the card's own
 * result; a column the result no longer has hides the chip. Absent keys take their defaults.
 */
export type AppKpiChange = {
  column: string;
  /** Default `percent`. */
  kind?: AppKpiChangeKind | null;
  /** `#rrggbb` for a rise; unset is the theme's green. Red for metrics where up is bad. */
  increase_color?: string | null;
  /** `#rrggbb` for a fall; unset is the theme's red. */
  decrease_color?: string | null;
  /** Default `md`, the chip's standard size. */
  size?: AppKpiFontSize | null;
  /** Shown after the number, e.g. "vs last week". */
  label?: string | null;
};

/**
 * How a KPI card looks. Every key is optional and an absent one keeps the default look. `rules`
 * are tried in order and the first match wins; a value matching none keeps the base colours.
 */
export type AppKpiDisplayConfig = {
  card_color?: string | null;
  text_color?: string | null;
  value_font_size?: AppKpiFontSize | null;
  label_font_size?: AppKpiFontSize | null;
  rules?: AppKpiColorRule[];
  /** Absent or null = no change chip. */
  change?: AppKpiChange | null;
};

/** What happens to the drill-down widget when its KPI card is deleted. */
export type AppKpiDrilldownDeleteAction = "unlink" | "delete";

export type AppKpiSummary = {
  id: number;
  app_id: number;
  name: string;
  /** Shown with the name in the card's info tooltip; null when the KPI has none. */
  description?: string | null;
  /** The KPI's SQL lives on its referenced AppQuery; not returned on the summary. */
  sql?: string | null;
  /** Reference to the reusable AppQuery that supplies this KPI's SQL. */
  query_id?: number | null;
  /** Which column of the single-row result to display; null = the only column. */
  value_column?: string | null;
  format: AppKpiFormat;
  /** How the card looks; null = the default look. */
  display_config?: AppKpiDisplayConfig | null;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  input_mode: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** The chart/table on this KPI's tab that opens in a dialog when the card is clicked. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
  created_at: string;
  updated_at: string;
};

export type AppKpiDataItem = {
  id: number;
  name: string;
  /** Shown with the name in the card's info tooltip; null when the KPI has none. */
  description?: string | null;
  format: AppKpiFormat;
  /** How the card looks; null = the default look. */
  display_config?: AppKpiDisplayConfig | null;
  column_span?: number;
  sort_order: number;
  tab_id?: number | null;
  value?: unknown;
  /** The change chip's raw number from the same result row; null hides the chip. */
  change_value?: unknown;
  error?: string | null;
  /** Display names of active tab filters that did not apply to this widget (drives the badge). */
  ignored_filters?: string[] | null;
  /**
   * Set by the server only when the pointer resolves to a live widget on this KPI's tab, so
   * "has a drill-down" can be read directly as "this card is clickable".
   */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
  /** Always false for a KPI — only charts and tables can be drilled into. */
  is_drilldown_target?: boolean;
  /** See `AppChartDataItem.comparison_periods`; dates a chip that has no label of its own. */
  comparison_periods?: AppComparisonPeriods | null;
};

export type AppKpisDataResponse = {
  kpis: AppKpiDataItem[];
};

const APP_KPI_FORMATS: readonly AppKpiFormat[] = ["number", "percent", "currency", "text"];

const APP_KPI_DRILLDOWN_ITEM_TYPES: readonly AppKpiDrilldownItemType[] = ["chart", "table"];

/**
 * Only the outer shape is checked: the server validates every key on write, and the card reads
 * each key defensively, so a bad entry falls back to the default look rather than failing the
 * whole response.
 */
function isOptionalDisplayConfig(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === "object" && !Array.isArray(value));
}

function isAppKpiFormat(value: unknown): value is AppKpiFormat {
  return typeof value === "string" && (APP_KPI_FORMATS as readonly string[]).includes(value);
}

/** Each half of the pointer must be absent, null, or the right type. */
function hasValidDrilldownPair(row: Record<string, unknown>): boolean {
  const itemType = row.drilldown_item_type;
  const itemId = row.drilldown_item_id;
  const typeOk =
    itemType === undefined ||
    itemType === null ||
    (typeof itemType === "string" &&
      (APP_KPI_DRILLDOWN_ITEM_TYPES as readonly string[]).includes(itemType));
  const idOk = itemId === undefined || itemId === null || typeof itemId === "number";
  return typeOk && idOk;
}

function isAppKpiSummary(value: unknown): value is AppKpiSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    typeof row.name === "string" &&
    (row.description === undefined ||
      row.description === null ||
      typeof row.description === "string") &&
    (row.sql === null || row.sql === undefined || typeof row.sql === "string") &&
    (row.query_id === undefined || row.query_id === null || typeof row.query_id === "number") &&
    (row.value_column === undefined ||
      row.value_column === null ||
      typeof row.value_column === "string") &&
    isAppKpiFormat(row.format) &&
    isOptionalDisplayConfig(row.display_config) &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    (row.input_mode === undefined || typeof row.input_mode === "string") &&
    (row.input_config === undefined ||
      row.input_config === null ||
      typeof row.input_config === "object") &&
    hasValidDrilldownPair(row) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppKpiDataItem(value: unknown): value is AppKpiDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (row.description === undefined ||
      row.description === null ||
      typeof row.description === "string") &&
    isAppKpiFormat(row.format) &&
    isOptionalDisplayConfig(row.display_config) &&
    (row.column_span === undefined || typeof row.column_span === "number") &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    hasValidDrilldownPair(row) &&
    (row.error === undefined || row.error === null || typeof row.error === "string")
  );
}

export async function fetchAppKpis(appId: number): Promise<AppKpiSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppKpiSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function fetchAppKpisData(appId: number): Promise<AppKpiDataItem[]> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis/data`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const kpis = (data as AppKpisDataResponse).kpis;
  if (!Array.isArray(kpis) || !kpis.every(isAppKpiDataItem)) {
    throw new Error("Unexpected response shape");
  }
  return kpis;
}

export type AddKpiToAppPayload = {
  name: string;
  description?: string | null;
  /** Provide exactly one of `sql` (inline) or `query_id` (reuse a saved query). */
  sql?: string;
  query_id?: number | null;
  /** Which column of the single-row result to display; required for multi-column results. */
  value_column?: string | null;
  format: AppKpiFormat;
  display_config?: AppKpiDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** Send both or neither; the target must already exist on `tab_id`. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export type UpdateAppKpiPayload = {
  name?: string;
  description?: string | null;
  sql?: string;
  query_id?: number | null;
  value_column?: string | null;
  format?: AppKpiFormat;
  /** Replaces the whole look; null resets the card to the default look. */
  display_config?: AppKpiDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** Send both as null to unlink the drill-down; omit both to leave it unchanged. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export async function addKpiToApp(
  appId: number,
  payload: AddKpiToAppPayload,
): Promise<AppKpiSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppKpiSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppKpi(
  appId: number,
  kpiId: number,
  payload: UpdateAppKpiPayload,
): Promise<AppKpiSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis/${kpiId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppKpiSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppKpi(
  appId: number,
  kpiId: number,
  drilldownAction: AppKpiDrilldownDeleteAction = "unlink",
): Promise<void> {
  const query = `?drilldown_action=${drilldownAction}`;
  const res = await apiFetch(`/v1/apps/${appId}/kpis/${kpiId}${query}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
